import { StyledEngineProvider, type Theme as MuiTheme, ThemeProvider } from '@mui/material/styles';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { applyTheme, resolveThemeName } from '@/themes';

import { getMuiTheme } from './muiTheme';

interface ThemeModeContextValue {
  // The active (resolved) theme name, e.g. "light-theme-viewnexa".
  themeName: string;
  // The MUI theme object for the active theme.
  muiTheme: MuiTheme;
  // Switches the theme; updates React state, body class, stylesheet and storage.
  setTheme: (name: string) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

// Public hook for reading/changing the current theme from React.
export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

interface ThemeModeProviderProps {
  children: ReactNode;
}

// Source of truth for the current theme. Holds the theme name in state
// (initialized from localStorage), applies the non-React side effects via
// applyTheme, and feeds the matching MUI theme object to ThemeProvider.
//
const ThemeModeProvider = ({ children }: ThemeModeProviderProps) => {
  const [themeName, setThemeName] = useState(() => resolveThemeName(localStorage.getItem('app-theme')));

  useEffect(() => {
    applyTheme(themeName);
  }, [themeName]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({
      themeName,
      muiTheme: getMuiTheme(themeName),
      setTheme: (name: string) => setThemeName(resolveThemeName(name)),
    }),
    [themeName]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={value.muiTheme}>{children}</ThemeProvider>
      </StyledEngineProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeProvider;
